import {FC, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Box} from '@chakra-ui/react';
import {hasValue, useT} from '@progress-fe/core';
import {ErrorBoundary} from 'ui-kit';
import {castTrees} from 'core/utils';
import {TProjectDatasetModel} from 'stores/TechProcessStore';
import {
  TTree,
  useToggle,
  SimpleTable,
  TreeListDialog,
  ISimpleTableColumnTarget
} from '@progress-fe/ui-kit';

import {HeaderRenderer, RowIdCellRenderer} from './components';

interface IProps {
  width: number;
  height: number;
  treeCatalog: Array<TTree>;
  datasetInstance: TProjectDatasetModel;
  onSelectDatasetRowKey: (rowKey: number) => Promise<void>;
}

const DatasetTableFC: FC<IProps> = ({
  width,
  height,
  treeCatalog,
  datasetInstance,
  onSelectDatasetRowKey
}) => {
  const {isOpen, open: openDialog, close: closeDialog} = useToggle({});
  const [selectedColumn, setSelectedColumn] = useState<ISimpleTableColumnTarget | null>(null);

  const {datasetTable, relationList, selectedRowKey} = datasetInstance;

  const {t} = useT();

  const handleOpenDialog = (column: ISimpleTableColumnTarget) => {
    setSelectedColumn(column);
    openDialog();
  };

  const handleCloseDialog = () => {
    setSelectedColumn(null);
    closeDialog();
  };

  return (
    <>
      {datasetTable && (
        <ErrorBoundary msg={t('common.datasetError')} height={'200px'}>
          <Box
            w={`${width}px`}
            maxH={`${height}px`}
            bg={'white'}
            borderRadius={'12px'}
            border={'1px solid'}
            borderColor={'accent50'}
            overflow={'hidden'}
          >
            <SimpleTable
              noBg
              isHeaderShownAlways
              rows={datasetTable.rows}
              tableSx={{w: `${width}px`, maxH: `${height}px`}}
              // @ts-ignore
              columns={datasetTable.columns.map((column, idx) => ({
                ...column,
                align: 'left',
                width: column.width ?? '150px',
                sticky: idx === 0 ? true : undefined,
                render:
                  idx === 0
                    ? (_, value, currentColumn, rowKey) => {
                        return (
                          <RowIdCellRenderer
                            value={value}
                            rowKey={rowKey}
                            column={currentColumn}
                            selectedRowKey={selectedRowKey}
                            onSelectRow={onSelectDatasetRowKey}
                          />
                        );
                      }
                    : undefined,
                renderHeader: (item: ISimpleTableColumnTarget) => (
                  <HeaderRenderer
                    item={item}
                    datasetInstance={datasetInstance}
                    onClick={() => handleOpenDialog(item)}
                  />
                )
              }))}
            />
          </Box>
        </ErrorBoundary>
      )}

      {isOpen && (
        <TreeListDialog
          trees={castTrees(treeCatalog)}
          activeItems={relationList?.filter((r) => r.columnName === selectedColumn?.name)}
          title={t('dataset.relateColumn', {name: selectedColumn?.name})}
          isPending={datasetInstance.isRelationChanging || datasetInstance.isRelationLoading}
          onClose={handleCloseDialog}
          onClickTreeItem={async (_, __, ___, data) => {
            const property = selectedColumn?.propertyName;
            if (hasValue(property) && hasValue(data.path)) {
              const foundRelation = relationList.find(
                (r) => r.jsonPath === data.path && r.columnName === property
              );
              if (foundRelation) {
                await datasetInstance.deleteRelation(foundRelation.uuid);
              } else {
                await datasetInstance.createOrChangeRelation(data.path, property);
              }
            }
          }}
        />
      )}
    </>
  );
};

export const DatasetTable = observer(DatasetTableFC);
